import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  SettingsOutlined as SettingsIcon,
  ShoppingCartOutlined as CreateOrderIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  FormControlLabel,
  Popover,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { ContactForm } from '@sophocles/components/contacts';
import FilterBar from '@sophocles/components/FilterBar';
import { DataGridToolbarShell, ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';

import ContactCardList from './ContactCardList';
import ContactGrid from './ContactGrid';
import ContactSearchBar from './ContactSearchBar';

const EMPTY_FILTERS = {
  has_address: false,
  is_primary_rma_billing: false,
  is_primary_rma_shipping: false,
  is_primary_sales_billing: false,
  is_primary_sales_shipping: false,
  has_email: false,
  has_phone: false,
  created_after: null,
  created_before: null,
  modified_after: null,
  modified_before: null,
};

const ContactManagementView = ({ customerId, siteGroupId, initialScopeFilter = 'all', lockScopeFilter = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isSuperUser } = useUser();
  const navigate = useNavigate();

  const [showDeleted, setShowDeleted] = useState(false);
  const [scopeFilter, setScopeFilter] = useState(initialScopeFilter);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteHandler, setBulkDeleteHandler] = useState(null);
  const [bulkRestoreHandler, setBulkRestoreHandler] = useState(null);
  const [selectionMode, setSelectionMode] = useState(null);

  const triggerRefresh = () => setRefreshTrigger(prev => prev + 1);

  const handleSelectionChange = (ids, deleteHandler, restoreHandler, mode) => {
    setSelectedIds(ids);
    setBulkDeleteHandler(() => deleteHandler);
    setBulkRestoreHandler(() => restoreHandler);
    setSelectionMode(mode);
  };

  const handleShowDeletedChange = newValue => {
    setSelectedIds([]);
    setShowDeleted(newValue);
    triggerRefresh();
  };

  const handleAddContact = () => {
    setSelectedContact(null);
    setDialogOpen(true);
  };

  const handleCreateOrder = () => {
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);
    if (siteGroupId) params.set('siteGroupId', siteGroupId);
    navigate(`/sales/orders/new?${params.toString()}`);
  };

  const handleEditContact = contact => {
    setSelectedContact(contact);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedContact(null);
  };

  const handleSaveSuccess = () => {
    handleDialogClose();
    triggerRefresh();
  };

  const handleSearchChange = newQuery => {
    setSearchQuery(newQuery);
    triggerRefresh();
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    triggerRefresh();
  };

  // FilterBar definitions — collapse the seven boolean flags into
  // two multi-selects (Has / Primary Contact) and the four date
  // params into two daterange filters. The shared FilterBar handles
  // the popover, active pills, and clear-all UX.
  const setFilterFields = patch => {
    setFilters(prev => ({ ...prev, ...patch }));
    triggerRefresh();
  };

  const hasOptions = useMemo(
    () => [
      { value: 'email', label: t('contacts.filters.hasEmail', 'Has email') },
      { value: 'phone', label: t('contacts.filters.hasPhone', 'Has phone') },
      { value: 'address', label: t('contacts.filters.hasAddress', 'Has address') },
    ],
    [t],
  );
  const primaryOptions = useMemo(
    () => [
      { value: 'rma_billing', label: t('contacts.filters.isPrimaryRmaBilling', 'RMA Billing') },
      { value: 'rma_shipping', label: t('contacts.filters.isPrimaryRmaShipping', 'RMA Shipping') },
      { value: 'sales_billing', label: t('contacts.filters.isPrimarySalesBilling', 'Sales Billing') },
      { value: 'sales_shipping', label: t('contacts.filters.isPrimarySalesShipping', 'Sales Shipping') },
    ],
    [t],
  );

  const hasValue = useMemo(() => {
    const v = [];
    if (filters.has_email) v.push('email');
    if (filters.has_phone) v.push('phone');
    if (filters.has_address) v.push('address');
    return v;
  }, [filters]);

  const primaryValue = useMemo(() => {
    const v = [];
    if (filters.is_primary_rma_billing) v.push('rma_billing');
    if (filters.is_primary_rma_shipping) v.push('rma_shipping');
    if (filters.is_primary_sales_billing) v.push('sales_billing');
    if (filters.is_primary_sales_shipping) v.push('sales_shipping');
    return v;
  }, [filters]);

  // Quick filters render inline as chip dropdowns (matches CustomerGrid /
  // SiteGrid). The two most-used contact filters live here.
  const quickFilters = useMemo(
    () => [
      {
        key: 'has',
        label: t('contacts.filters.has', 'Has'),
        type: 'multi',
        options: hasOptions,
        value: hasValue,
        onChange: next => {
          const set = new Set(next || []);
          setFilterFields({
            has_email: set.has('email'),
            has_phone: set.has('phone'),
            has_address: set.has('address'),
          });
        },
      },
      {
        key: 'primary',
        label: t('contacts.filters.primary', 'Primary'),
        type: 'multi',
        options: primaryOptions,
        value: primaryValue,
        onChange: next => {
          const set = new Set(next || []);
          setFilterFields({
            is_primary_rma_billing: set.has('rma_billing'),
            is_primary_rma_shipping: set.has('rma_shipping'),
            is_primary_sales_billing: set.has('sales_billing'),
            is_primary_sales_shipping: set.has('sales_shipping'),
          });
        },
      },
    ],
    [t, hasOptions, hasValue, primaryOptions, primaryValue],
  );

  const moreFilters = useMemo(() => {
    const defs = [
      {
        key: 'created',
        label: t('contacts.filters.created', 'Created'),
        type: 'daterange',
        value:
          filters.created_after || filters.created_before
            ? { from: filters.created_after, to: filters.created_before }
            : null,
        onChange: next => {
          setFilterFields({
            created_after: next?.from ?? null,
            created_before: next?.to ?? null,
          });
        },
      },
      {
        key: 'modified',
        label: t('contacts.filters.modified', 'Modified'),
        type: 'daterange',
        value:
          filters.modified_after || filters.modified_before
            ? { from: filters.modified_after, to: filters.modified_before }
            : null,
        onChange: next => {
          setFilterFields({
            modified_after: next?.from ?? null,
            modified_before: next?.to ?? null,
          });
        },
      },
    ];
    if (isSuperUser) {
      defs.push({
        key: 'show_deleted',
        label: t('contacts.management.showDeleted', 'Show deleted'),
        type: 'boolean',
        value: showDeleted ? 'true' : null,
        onChange: next => handleShowDeletedChange(next === 'true'),
      });
    }
    return defs;
  }, [t, filters, isSuperUser, showDeleted]);

  const handleResetAll = () => {
    setFilters(EMPTY_FILTERS);
    if (showDeleted) handleShowDeletedChange(false);
    triggerRefresh();
  };

  const showScopeToggle = !lockScopeFilter && siteGroupId && customerId;
  const settingsAnchorRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <Box>
      <DataGridToolbarShell variant="flat" sx={{ justifyContent: 'space-between' }}>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ flex: 1, minWidth: 0, gap: 'var(--toolbar-gap)' }}
        >
          <FilterBar quickFilters={quickFilters} moreFilters={moreFilters} onResetAll={handleResetAll} />

          <ContactSearchBar value={searchQuery} onChange={handleSearchChange} onClear={handleSearchClear} />
        </Stack>

        <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 'var(--toolbar-gap)' }}>
          {/* Bulk Delete / Restore — reserved width so the toolbar doesn't reflow when selection state changes. */}
          <Button
            variant="outlined"
            size="small"
            color={selectionMode === 'deleted' ? 'primary' : 'error'}
            startIcon={selectionMode === 'deleted' ? <RestoreIcon /> : <DeleteIcon />}
            onClick={() => {
              if (selectionMode === 'deleted') {
                bulkRestoreHandler && bulkRestoreHandler();
              } else {
                bulkDeleteHandler && bulkDeleteHandler();
              }
            }}
            disabled={selectedIds.length === 0}
            sx={{ visibility: selectedIds.length > 0 ? 'visible' : 'hidden' }}
          >
            {selectionMode === 'deleted'
              ? t('contacts.management.restoreSelected', 'Restore')
              : t('contacts.management.deleteSelected', 'Delete')}{' '}
            ({selectedIds.length || 0})
          </Button>

          {showScopeToggle ? (
            <ToolbarIconButton
              ref={settingsAnchorRef}
              tooltip={t('contacts.management.viewSettings', 'View settings')}
              onClick={() => setSettingsOpen(true)}
            >
              <SettingsIcon sx={{ fontSize: 18 }} />
            </ToolbarIconButton>
          ) : null}

          {customerId && (
            <Button variant="outlined" size="small" startIcon={<CreateOrderIcon />} onClick={handleCreateOrder}>
              {t('contacts.management.createOrder', 'Create Order')}
            </Button>
          )}
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddContact}>
            {t('contacts.management.addButton', 'Add Contact')}
          </Button>
        </Stack>
      </DataGridToolbarShell>

      {showScopeToggle ? (
        <Popover
          open={settingsOpen}
          anchorEl={settingsAnchorRef.current}
          onClose={() => setSettingsOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 0.5, p: 1.5, minWidth: 280 } } }}
        >
          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Switch
                checked={scopeFilter === 'all'}
                onChange={e => setScopeFilter(e.target.checked ? 'all' : 'site_group')}
              />
            }
            label={
              <Typography variant="body2">
                {t('contacts.scope.showCustomerContacts', 'Show customer-level contacts')}
              </Typography>
            }
          />
        </Popover>
      ) : null}

      <Box sx={{ mt: 2 }}>
        {isMobile ? (
          <ContactCardList
            key={refreshTrigger}
            customerId={customerId}
            siteGroupId={siteGroupId}
            showDeleted={showDeleted}
            onEditContact={handleEditContact}
            scopeFilter={scopeFilter}
            searchQuery={searchQuery}
            filters={filters}
          />
        ) : (
          <ContactGrid
            key={refreshTrigger}
            customerId={customerId}
            siteGroupId={siteGroupId}
            showDeleted={showDeleted}
            onEditContact={handleEditContact}
            scopeFilter={scopeFilter}
            searchQuery={searchQuery}
            filters={filters}
            onSelectionChange={handleSelectionChange}
          />
        )}
      </Box>

      <ContactForm
        open={dialogOpen}
        contact={selectedContact}
        customerId={customerId}
        siteGroupId={siteGroupId}
        onClose={handleDialogClose}
        onSuccess={handleSaveSuccess}
        defaultSaveLevel={siteGroupId ? 'site_group' : 'customer'}
        primaryContext="all"
      />
    </Box>
  );
};

ContactManagementView.propTypes = {
  customerId: PropTypes.number,
  siteGroupId: PropTypes.number,
  initialScopeFilter: PropTypes.oneOf(['all', 'customer', 'site_group']),
  lockScopeFilter: PropTypes.bool,
};

export default ContactManagementView;
